import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Trash, Warning, FloppyDisk, CalendarBlank, User, Package, Note,
} from "@phosphor-icons/react";
import SignaturePad from "@/components/SignaturePad";
import { useAuth } from "@/lib/auth";

const EMPTY_PRODUCT = { sku: "", vpn: "", series: "", pieces: 0 };

export default function NewRechazo() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [providers, setProviders] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [causesCatalog, setCausesCatalog] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    return {
      date: `${y}-${m}-${d}`,
      time: `${hh}:${mm}`,
      provider_name: "",
      appointment_folio: "",
      selected_causes: new Set(),
      custom_cause: "",
      products: [{ ...EMPTY_PRODUCT }],
      comments: "",
      receiver_name: "",
      receiver_signature: "",
      deliverer_name: "",
      deliverer_signature: "",
    };
  });

  const recvSigRef = useRef(null);
  const delvSigRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [p, a, c] = await Promise.all([
          api.get("/catalogs/providers"),
          api.get("/appointments"),
          api.get("/catalogs/rejection-causes"),
        ]);
        setProviders(p.data);
        setAppointments(a.data);
        setCausesCatalog(c.data);
      } catch (err) {
        console.debug("[NewRechazo] load failed", err);
      }
    })();
  }, []);

  const toggleCause = (label) => {
    setForm((f) => {
      const next = new Set(f.selected_causes);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return { ...f, selected_causes: next };
    });
  };

  const updateProduct = (idx, patch) => {
    setForm((f) => ({
      ...f,
      products: f.products.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    }));
  };
  const addProduct = () =>
    setForm((f) => ({ ...f, products: [...f.products, { ...EMPTY_PRODUCT }] }));
  const removeProduct = (idx) =>
    setForm((f) => ({
      ...f,
      products: f.products.length === 1 ? f.products : f.products.filter((_, i) => i !== idx),
    }));

  const totalPieces = useMemo(
    () => form.products.reduce((s, p) => s + (Number(p.pieces) || 0), 0),
    [form.products],
  );

  const canSave = form.provider_name.trim() && (form.selected_causes.size > 0 || form.custom_cause.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) {
      toast.error("Ingresa proveedor y al menos una causa de rechazo");
      return;
    }
    setSaving(true);
    try {
      const iso = new Date(`${form.date}T${form.time || "00:00"}:00`).toISOString();
      const body = {
        elaboration_date: iso,
        provider_name: form.provider_name.trim(),
        appointment_folio: form.appointment_folio.trim(),
        causes: Array.from(form.selected_causes),
        custom_cause: form.custom_cause.trim(),
        products: form.products
          .filter((p) => (p.sku || p.vpn || p.series || Number(p.pieces) > 0))
          .map((p) => ({
            sku: p.sku || "",
            vpn: p.vpn || "",
            series: p.series || "",
            pieces: Number(p.pieces) || 0,
          })),
        comments: form.comments,
        receiver_name: form.receiver_name,
        receiver_signature: form.receiver_signature,
        deliverer_name: form.deliverer_name,
        deliverer_signature: form.deliverer_signature,
      };
      const { data } = await api.post("/rejection-records", body);
      toast.success(`Rechazo ${data.folio} guardado`);
      nav(`/rechazos/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] text-[#0055FF] mb-2">
          Rechazo · Nuevo Registro
        </div>
        <h1 className="font-display text-4xl sm:text-5xl flex items-center gap-3">
          <Warning size={36} weight="duotone" className="text-[#FF3B30]" />
          Rechazo por Producto Maltratado
        </h1>
        <p className="text-white/50 text-sm mt-2 max-w-2xl">
          Registra una devolución oficial con causas, SKUs afectados, firmas y comentarios.
          Se genera un folio RCH automático y un PDF con logo Ingram Micro.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        {/* Datos generales */}
        <Section title="Datos generales" icon={CalendarBlank}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Fecha">
              <input
                type="date"
                data-testid="rch-date"
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                className="input-dark"
              />
            </Field>
            <Field label="Hora">
              <input
                type="time"
                data-testid="rch-time"
                value={form.time}
                onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                className="input-dark"
              />
            </Field>
            <Field label="Proveedor">
              <input
                list="providers-list"
                data-testid="rch-provider"
                value={form.provider_name}
                onChange={(e) => setForm((f) => ({ ...f, provider_name: e.target.value }))}
                placeholder="Escribe o selecciona…"
                className="input-dark"
              />
              <datalist id="providers-list">
                {providers.map((p) => (<option key={p.id} value={p.name} />))}
              </datalist>
            </Field>
            <Field label="Núm. de Cita (folio RCN)">
              <input
                list="appts-list"
                data-testid="rch-appointment"
                value={form.appointment_folio}
                onChange={(e) => setForm((f) => ({ ...f, appointment_folio: e.target.value }))}
                placeholder="RCN…"
                className="input-dark"
              />
              <datalist id="appts-list">
                {appointments.map((a) => (
                  <option key={a.id} value={a.folio}>{`${a.folio} · ${a.provider_name}`}</option>
                ))}
              </datalist>
            </Field>
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-white/50">
            <User size={13} weight="duotone" className="text-[#0055FF]" />
            Elaborado por <span className="text-white font-mono-data">{user?.username}</span>
          </div>
        </Section>

        {/* Causas */}
        <Section title="Causas del Rechazo" icon={Warning}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {causesCatalog.map((c) => {
              const checked = form.selected_causes.has(c.label);
              return (
                <label
                  key={c.id}
                  data-testid={`rch-cause-${c.id}`}
                  className={`flex items-start gap-2 p-3 rounded-sm border cursor-pointer transition-colors ${
                    checked ? "bg-[#0055FF]/10 border-[#0055FF]" : "bg-[#0A0A0A] border-white/10 hover:border-white/25"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleCause(c.label)}
                    className="mt-0.5 accent-[#0055FF]"
                  />
                  <span className="text-sm text-white">{c.label}</span>
                </label>
              );
            })}
          </div>
          <div className="mt-3">
            <Field label="Otros (texto libre — este se guardará en el PDF)">
              <input
                data-testid="rch-custom-cause"
                value={form.custom_cause}
                onChange={(e) => setForm((f) => ({ ...f, custom_cause: e.target.value }))}
                placeholder="Escribe otra causa si no está en la lista…"
                className="input-dark"
              />
            </Field>
            <p className="text-[10px] text-white/40 mt-1">
              Tip: si esta causa se repite, pídele al admin que la agregue en Catálogos → Causas de Rechazo.
            </p>
          </div>
        </Section>

        {/* Productos */}
        <Section title="Detalle de Productos" icon={Package}>
          <div className="overflow-x-auto rounded-sm border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] text-[9px] font-mono-data uppercase tracking-widest text-white/40">
                <tr>
                  <th className="text-left px-3 py-2">SKU</th>
                  <th className="text-left px-3 py-2">VPN</th>
                  <th className="text-left px-3 py-2">Series</th>
                  <th className="text-right px-3 py-2 w-24">PZ</th>
                  <th className="px-2 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody data-testid="rch-products">
                {form.products.map((p, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="px-2 py-1">
                      <input value={p.sku} onChange={(e) => updateProduct(i, { sku: e.target.value })} className="input-cell" />
                    </td>
                    <td className="px-2 py-1">
                      <input value={p.vpn} onChange={(e) => updateProduct(i, { vpn: e.target.value })} className="input-cell" />
                    </td>
                    <td className="px-2 py-1">
                      <input value={p.series} onChange={(e) => updateProduct(i, { series: e.target.value })} className="input-cell" />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="number" min="0"
                        value={p.pieces}
                        onChange={(e) => updateProduct(i, { pieces: e.target.value })}
                        className="input-cell text-right"
                      />
                    </td>
                    <td className="px-1 py-1 text-center">
                      <button
                        type="button"
                        onClick={() => removeProduct(i)}
                        disabled={form.products.length === 1}
                        className="p-1.5 rounded-sm text-white/40 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-30 disabled:hover:bg-transparent"
                      >
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                <tr className="border-t border-white/10 bg-[#0A0A0A]">
                  <td className="px-3 py-2 text-xs text-white/40" colSpan={3}>
                    <button
                      type="button"
                      onClick={addProduct}
                      data-testid="rch-add-product"
                      className="inline-flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-widest text-[#0055FF] hover:text-white"
                    >
                      <Plus size={13} /> Agregar producto
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right font-mono-data text-white/80 font-bold">
                    Total: {totalPieces}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </Section>

        {/* Comentarios */}
        <Section title="Comentarios Generales" icon={Note}>
          <textarea
            data-testid="rch-comments"
            value={form.comments}
            onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
            rows={4}
            placeholder="Observaciones, folios de referencia, contexto…"
            className="input-dark w-full resize-y"
          />
        </Section>

        {/* Firmas */}
        <Section title="Firmas" icon={User}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
                Quien ENTREGA el rechazo
              </label>
              <input
                data-testid="rch-deliverer-name"
                value={form.deliverer_name}
                onChange={(e) => setForm((f) => ({ ...f, deliverer_name: e.target.value }))}
                placeholder="Nombre completo"
                className="input-dark mb-2"
              />
              <SignaturePad
                ref={delvSigRef}
                testId="rch-deliverer-sign"
                onChange={(dataURL) => setForm((f) => ({ ...f, deliverer_signature: dataURL }))}
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
                Quien RECIBE el rechazo
              </label>
              <input
                data-testid="rch-receiver-name"
                value={form.receiver_name}
                onChange={(e) => setForm((f) => ({ ...f, receiver_name: e.target.value }))}
                placeholder="Nombre completo"
                className="input-dark mb-2"
              />
              <SignaturePad
                ref={recvSigRef}
                testId="rch-receiver-sign"
                onChange={(dataURL) => setForm((f) => ({ ...f, receiver_signature: dataURL }))}
              />
            </div>
          </div>
        </Section>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => nav("/rechazos")}
            className="px-4 py-2.5 rounded-sm border border-white/10 text-sm text-white/70 hover:bg-white/5 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSave || saving}
            data-testid="rch-save"
            className="flex items-center gap-2 px-5 py-2.5 rounded-sm bg-[#0055FF] text-white text-sm font-semibold hover:brightness-110 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-transform"
          >
            <FloppyDisk size={16} weight="fill" />
            {saving ? "Guardando…" : "Guardar y generar folio"}
          </button>
        </div>
      </form>

      <style>{`
        .input-dark {
          width: 100%;
          padding: 0.5rem 0.6rem;
          border-radius: 2px;
          background: #0A0A0A;
          border: 1px solid rgba(255,255,255,0.1);
          color: white;
          font-size: 0.8rem;
          outline: none;
        }
        .input-dark:focus { border-color: #0055FF; box-shadow: 0 0 0 1px #0055FF; }
        .input-cell {
          width: 100%;
          padding: 0.35rem 0.5rem;
          background: transparent;
          border: 1px solid transparent;
          color: white;
          font-size: 0.8rem;
          outline: none;
          font-family: 'Consolas', monospace;
        }
        .input-cell:focus { border-color: #0055FF; background: rgba(0,85,255,0.05); }
      `}</style>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-[#111111] border border-white/10 rounded-sm">
      <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
        <Icon size={16} weight="duotone" className="text-[#0055FF]" />
        <div className="font-mono-data text-[11px] uppercase tracking-widest text-white/80">{title}</div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
